/**
 * <popup-messages> — site-wide info popups. See sections/popup-messages.liquid (markup + THE match rule)
 * and docs/00-features/SALES/popup-messages/popup-messages-plan.md.
 *
 * One host, one hidden .popup-msg[data-entry="<handle>"] per metaobject entry. This file never decides
 * "is this a match?" — the Liquid does. It only decides WHEN to open WHICH entry.
 *
 * TRIGGERS
 *   1. Page visit — entries with data-page-match="true" (this page is one of their trigger pages).
 *   2. Any ADD    — watch the browser's network log for /cart/add (PerformanceObserver, read-only: no
 *                   fetch/XHR wrapping — Intelligems + UpCart + Shopify already wrap those). Then re-read
 *                   /cart.js, diff against the last snapshot (keyed by line key) to see which products
 *                   actually went UP, and ask the section — Section Rendering, /products/<handle>?sections=<id>
 *                   — which entries each one matches (the host's data-matches).
 *   Why the network log and not UpCart's events (live-tested 2026-09-10): UpCart's AddedToCart misses
 *   upsell adds and background adds. Every add hits /cart/add; the drawer's "+" hits /cart/change — so
 *   quantity bumps never fire. /cart/change|update|clear still refresh the snapshot so a later diff stays honest.
 *   Collection targeting is ADD-ONLY — a matching PDP does NOT open on load (team call, 2026-10-19: the
 *   PDP's pre-sale accordion + callout already cover the visit). Its data-product-match still makes the
 *   Liquid load the cover image eagerly there, so the popup is ready when the add lands.
 *
 * ONCE RULES (same for every entry — no per-entry setting; revisit if a non-pre-sale popup ever needs a
 * different frequency):
 *   - Page visit: at most once per ENTRY per page view.
 *   - Add: at most once per entry per PRODUCT per page view (team call, 2026-11-03 — each new pre-sale
 *     item gets its own notice). Another size of the same product = silent; a different product of the
 *     same entry = fires again; several new products of one entry in ONE add (Kaching set) = one popup.
 *     A page-visit showing does not silence adds.
 *   A new page view starts fresh. History: once per product until 2026-09-22, once per entry until 2026-11-03.
 *
 * DATE WINDOW: data-start / data-end (epoch seconds, end exclusive) are checked HERE, at trigger time —
 * Shopify caches rendered pages, so Liquid never knows "now". Empty = no limit; end <= start = never.
 *
 * PAGE HOLD: on-load popups wait while any [data-popup-hold] element exists (released via `popup:release`,
 * capped at HOLD_MAX_MS) — so a page's own load animation plays first. See waitForHold().
 *
 * QUEUE: one popup at a time. Several entries on one trigger open one after another (close → next). The
 * queue owns the scroll lock and the focus: locked on the first open, kept through every hand-off,
 * released (+ focus back to where it was) only when the queue is empty — so the page never jumps between two.
 *
 * Fail-closed everywhere: any fetch/parse problem = no popup, never a broken page.
 * Closes on: the X, the CTA, the overlay, Escape — anything carrying [data-popup-msg-close].
 * ⚠️ Escape is swallowed while open (window, CAPTURE phase — runs before anyone else's listener), so it
 * closes ONLY the popup; UpCart also closes its drawer on Escape and it sits right behind us on add.
 *
 * DEV / QA
 *   ?popup_preview=<entry handle>  opens that entry (ignores dates + memory). ?popup_preview=1 = the first.
 *   ?popup_now=2026-09-28T23:59    pretends it's that moment, wall clock in the STORE timezone (the host's
 *                                  data-tz-offset); a trailing Z or ±HH:MM uses that offset instead.
 * MANUAL TRIGGER: any [data-ss-modal-open="popup-message:<handle>"] (the shared ss-modal-trigger snippet with
 * modal_id 'popup-message:<handle>') opens that entry, always. ss-modal.js finds no template for that id and
 * steps aside, so this is the only listener that acts on it.
 */
(function () {
  if (customElements.get('popup-messages')) return;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var DURATION = 350; // matches the 0.35s CSS transition
  var LOAD_OPEN_DELAY = 600; // page visit / PDP: let the page paint first (counted after any page hold)
  var HOLD_MAX_MS = 8000; // a page hold never blocks longer than this — a script error must not eat the popup
  var ADD_OPEN_DELAY = 450; // at least UpCart's drawer slide-in — land on a settled drawer
  var ADD_RE = /\/cart\/add(\.js)?$/;
  var MUTATE_RE = /\/cart\/(change|update|clear)(\.js)?$/;
  var MATCHES_RE = /<popup-messages\b[^>]*\bdata-matches="([^"]*)"/;
  var MANUAL_PREFIX = 'popup-message:';
  var MANUAL_TRIGGER = '[data-ss-modal-open^="' + MANUAL_PREFIX + '"]';

  class PopupMessages extends HTMLElement {
    connectedCallback() {
      this.entries = {}; // handle -> .popup-msg element
      this.order = []; // handles, in render order
      var els = this.querySelectorAll('.popup-msg[data-entry]');
      for (var i = 0; i < els.length; i++) {
        var handle = els[i].getAttribute('data-entry');
        if (!handle || this.entries[handle]) continue;
        this.entries[handle] = els[i];
        this.order.push(handle);
      }
      if (!this.order.length) return;

      this.current = null; // handle of the open entry
      this.pending = []; // handles waiting to open, in order
      this.handoffTimer = null; // between a close and the next open
      this.locked = false; // did WE add the scroll lock (so we only remove our own)
      this.lastFocused = null; // focus to restore when the queue empties
      this.hideTimers = {};

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onManualTrigger = this.onManualTrigger.bind(this);
      this.addEventListener('click', this.onClick);
      // Before the editor branch on purpose — a manual trigger works in the theme editor too.
      document.addEventListener('click', this.onManualTrigger);

      // THEME EDITOR: open the first entry while the section is selected in the sidebar, close on deselect.
      // No storefront triggers here.
      if (window.Shopify && window.Shopify.designMode) {
        this.initDesignMode();
        return;
      }

      var params = new URLSearchParams(window.location.search);
      this.nowOverride = this.parseNow(params.get('popup_now'));

      this.initTriggers();

      var preview = params.get('popup_preview');
      if (preview !== null) {
        if (this.entries[preview]) this.open(preview);
        else if (preview === '1' || preview === '') this.open(this.order[0]);
      }
    }

    disconnectedCallback() {
      // The editor replaces this element on every settings change — never leave the page locked.
      window.removeEventListener('keydown', this.onKeydown, true);
      document.removeEventListener('click', this.onManualTrigger);
      if (this.locked) document.documentElement.classList.remove('ss-scroll-lock');
      clearTimeout(this.handoffTimer);
      for (var h in this.hideTimers) clearTimeout(this.hideTimers[h]);
      if (this.observer) this.observer.disconnect();
      if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
      if (this._onEditorSelect) document.removeEventListener('shopify:section:select', this._onEditorSelect);
      if (this._onEditorDeselect) document.removeEventListener('shopify:section:deselect', this._onEditorDeselect);
    }

    /* ---------------- Queue: open / close ---------------- */

    // Public: queue one entry, no rules (preview, manual trigger, editor).
    open(handle) {
      this.enqueue([handle]);
    }

    enqueue(handles) {
      for (var i = 0; i < handles.length; i++) {
        var h = handles[i];
        if (!this.entries[h] || h === this.current || this.pending.indexOf(h) !== -1) continue;
        this.pending.push(h);
      }
      if (!this.current && !this.handoffTimer) this.next();
    }

    next() {
      var h = this.pending.shift();
      if (h) this.show(h);
      else this.release();
    }

    show(handle) {
      var el = this.entries[handle];
      this.current = handle;
      clearTimeout(this.hideTimers[handle]); // reopened mid-close

      if (!this.lastFocused) this.lastFocused = document.activeElement; // first of this run only
      el.hidden = false;
      // Force a style flush so the transition runs from the closed state (we were display:none).
      void el.offsetWidth;
      el.classList.add('is-open');

      if (!document.documentElement.classList.contains('ss-scroll-lock')) {
        document.documentElement.classList.add('ss-scroll-lock');
        this.locked = true;
      }

      // Focus the dialog itself (tabindex="-1"), not the X — a11y focus move without a ring flash.
      var dialog = this.dialogOf(handle);
      if (dialog) dialog.focus({ preventScroll: true });
      window.addEventListener('keydown', this.onKeydown, true);
    }

    close() {
      var handle = this.current;
      if (!handle) return;
      var el = this.entries[handle];
      this.current = null;
      el.classList.remove('is-open');
      window.removeEventListener('keydown', this.onKeydown, true);

      // Back to display:none after the fade, unless reopened meanwhile.
      var self = this;
      this.hideTimers[handle] = setTimeout(function () {
        if (self.current !== handle) el.hidden = true;
      }, DURATION);

      if (!this.pending.length) {
        this.release();
        return;
      }
      // Hand-off: keep the lock + the original focus target; open the next one once this one has faded.
      this.handoffTimer = setTimeout(function () {
        self.handoffTimer = null;
        self.next();
      }, DURATION);
    }

    // Give the page back: our scroll lock + focus to whatever had it before the first popup opened.
    release() {
      if (this.locked) {
        document.documentElement.classList.remove('ss-scroll-lock');
        this.locked = false;
      }
      var target = this.lastFocused;
      this.lastFocused = null;
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    }

    dialogOf(handle) {
      var el = this.entries[handle];
      return el ? el.querySelector('.popup-msg__dialog') : null;
    }

    onClick(e) {
      if (e.target.closest('[data-popup-msg-close]')) this.close();
    }

    onManualTrigger(e) {
      var trigger = e.target.closest(MANUAL_TRIGGER);
      if (!trigger) return;
      var handle = trigger.getAttribute('data-ss-modal-open').slice(MANUAL_PREFIX.length);
      if (!this.entries[handle]) return;
      e.preventDefault();
      this.open(handle);
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.close();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    }

    trapFocus(e) {
      var dialog = this.dialogOf(this.current);
      if (!dialog) return;
      var nodes = dialog.querySelectorAll(FOCUSABLE);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      // We focus the dialog container on open; it isn't in `nodes`, so snap to an edge.
      if (document.activeElement === dialog) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---------------- Rules: date window + once per page view ---------------- */

    now() {
      return this.nowOverride != null ? this.nowOverride : Date.now();
    }

    inWindow(handle) {
      var el = this.entries[handle];
      var start = parseInt(el.getAttribute('data-start'), 10) * 1000; // NaN when empty
      var end = parseInt(el.getAttribute('data-end'), 10) * 1000;
      var now = this.now();
      if (start && end && end <= start) return false;
      if (start && now < start) return false;
      if (end && now >= end) return false;
      return true;
    }

    // Once per page view — per entry (page visit) or per entry + product (add). Decided (and remembered)
    // at TRIGGER time, so an add that lands during an open delay can't show the same thing twice.
    take(handle, productId) {
      var key = productId ? handle + ':' + productId : handle;
      if (this.shown[key] || !this.inWindow(handle)) return false;
      this.shown[key] = true;
      return true;
    }

    // "2026-09-28T23:59" (store wall clock, via the host's data-tz-offset) or with its own "Z" / "±HH:MM".
    // Parsed by hand — Date's own string parsing is inconsistent across browsers (Safari).
    parseNow(value) {
      if (!value) return null;
      // URLSearchParams turns a "+" in the offset into a space.
      var m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?(Z|[ +-]\d{2}:?\d{2})?$/.exec(value.trim());
      if (!m) return null;
      var wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
      var offset = m[7] ? m[7].replace(' ', '+') : this.getAttribute('data-tz-offset');
      return wall - this.offsetMinutes(offset) * 60000;
    }

    offsetMinutes(s) {
      var m = /^([+-])(\d{2}):?(\d{2})$/.exec(s || '');
      if (!m) return 0; // "Z" or unknown → UTC
      var minutes = +m[2] * 60 + +m[3];
      return m[1] === '-' ? -minutes : minutes;
    }

    /* ---------------- Triggers ---------------- */

    initTriggers() {
      var self = this;
      this.sectionId = this.getAttribute('data-section-id') || 'popup-messages';
      this.root = (this.getAttribute('data-root') || '/').replace(/\/$/, ''); // "" or "/en-ca"
      this.shown = {}; // "handle" (page visit) or "handle:productId" (add) -> true — THIS page view only
      this.matchCache = {}; // product handle -> Promise<entry handles[]>, this page view only
      this.cartQueue = Promise.resolve(); // cart reads run one at a time, in order
      this.snapshot = this.readSnapshot();

      // 1. Page visit. Collection matches never open on load — on a matching PDP the add (2) opens it.
      var onLoad = [];
      this.order.forEach(function (h) {
        if (self.entries[h].getAttribute('data-page-match') === 'true' && self.take(h)) onLoad.push(h);
      });
      if (onLoad.length) {
        this.waitForHold().then(function () {
          setTimeout(function () {
            self.enqueue(onLoad);
          }, LOAD_OPEN_DELAY);
        });
      }

      // 2. Any add
      if (!('PerformanceObserver' in window)) return;
      this.observer = new PerformanceObserver(function (list) {
        self.onEntries(list.getEntries());
      });
      try {
        this.observer.observe({ type: 'resource' });
      } catch (err) {
        return; // very old browser — the load triggers still work
      }

      // No Liquid snapshot (shouldn't happen) → take a baseline now, so the first add isn't misread.
      if (!this.snapshot) this.syncCart(false);

      // Back/forward cache restores a frozen page — the cart may have moved on meanwhile.
      this._onPageShow = function (e) {
        if (e.persisted) self.syncCart(false);
      };
      window.addEventListener('pageshow', this._onPageShow);
    }

    // PAGE HOLD — a page with its own load interaction (e.g. the promo hero intro, sections/promo-hero-v2)
    // marks an element [data-popup-hold] BEFORE this script runs (inline, during parse), then removes it and
    // dispatches `popup:release` on document when done. On-load popups wait for that; adds never do.
    // No hold on the page = resolves at once (every other page is unchanged). Capped at HOLD_MAX_MS.
    waitForHold() {
      return new Promise(function (resolve) {
        if (!document.querySelector('[data-popup-hold]')) return resolve();
        var timer;
        var finish = function () {
          document.removeEventListener('popup:release', check);
          clearTimeout(timer);
          resolve();
        };
        var check = function () {
          if (!document.querySelector('[data-popup-hold]')) finish();
        };
        document.addEventListener('popup:release', check);
        timer = setTimeout(finish, HOLD_MAX_MS);
      });
    }

    readSnapshot() {
      var el = this.querySelector('script[data-popup-cart]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent) || null;
      } catch (err) {
        return null;
      }
    }

    onEntries(entries) {
      var isAdd = false;
      var isMutation = false;
      for (var i = 0; i < entries.length; i++) {
        var type = entries[i].initiatorType;
        if (type !== 'fetch' && type !== 'xmlhttprequest') continue;
        var path;
        try {
          path = new URL(entries[i].name).pathname;
        } catch (err) {
          continue;
        }
        if (ADD_RE.test(path)) isAdd = true;
        else if (MUTATE_RE.test(path)) isMutation = true;
      }
      if (isAdd || isMutation) this.syncCart(isAdd);
    }

    syncCart(isAdd) {
      var self = this;
      var startedAt = Date.now();
      this.cartQueue = this.cartQueue
        .then(function () {
          return self.sync(isAdd, startedAt);
        })
        .catch(function () {}); // fail closed
    }

    // Re-read the cart, update the snapshot, and — for an add — find which products went up and check them.
    sync(isAdd, startedAt) {
      var self = this;
      return fetch(this.root + '/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var prev = self.snapshot || {};
          var next = {};
          var candidates = [];
          var seen = {};

          cart.items.forEach(function (item) {
            next[item.key] = item.quantity;
            if (!isAdd || item.quantity <= (prev[item.key] || 0)) return;
            var id = String(item.product_id);
            if (seen[id]) return; // one check per product
            seen[id] = true;
            candidates.push(item);
          });

          self.snapshot = next;
          if (!candidates.length) return;

          return Promise.all(
            candidates.map(function (item) {
              return self.matchesFor(item.handle).then(function (handles) {
                return handles;
              });
            })
          ).then(function (results) {
            // Each new product is remembered on its own; several of one entry in ONE add = that entry once.
            var toShow = [];
            results.forEach(function (handles, i) {
              var productId = candidates[i].product_id;
              handles.forEach(function (h) {
                if (self.entries[h] && self.take(h, productId) && toShow.indexOf(h) === -1) toShow.push(h);
              });
            });
            if (!toShow.length) return;
            var wait = Math.max(0, ADD_OPEN_DELAY - (Date.now() - startedAt));
            setTimeout(function () {
              self.enqueue(toShow);
            }, wait);
          });
        });
    }

    // Asks the section, rendered for that product: which entries does it match? (host data-matches)
    matchesFor(handle) {
      if (!handle) return Promise.resolve([]);
      if (!this.matchCache[handle]) {
        var sectionId = this.sectionId;
        var url =
          this.root + '/products/' + encodeURIComponent(handle) + '?sections=' + encodeURIComponent(sectionId);
        this.matchCache[handle] = fetch(url)
          .then(function (r) {
            return r.ok ? r.json() : {};
          })
          .then(function (data) {
            var html = data && data[sectionId];
            var m = typeof html === 'string' ? MATCHES_RE.exec(html) : null;
            return m ? m[1].split(' ').filter(Boolean) : [];
          })
          .catch(function () {
            return [];
          });
      }
      return this.matchCache[handle];
    }

    /* ---------------- Theme editor ---------------- */

    initDesignMode() {
      var self = this;
      var wrapperId = 'shopify-section-' + (this.getAttribute('data-section-id') || '');

      // `shopify:section:select` re-fires after a re-render while still selected — keeps the popup on
      // screen instead of vanishing on every settings change.
      this._onEditorSelect = function (e) {
        if (e.target.id === wrapperId) self.open(self.order[0]);
      };
      this._onEditorDeselect = function (e) {
        if (e.target.id !== wrapperId) return;
        self.pending = [];
        self.close();
      };

      document.addEventListener('shopify:section:select', this._onEditorSelect);
      document.addEventListener('shopify:section:deselect', this._onEditorDeselect);
    }
  }

  customElements.define('popup-messages', PopupMessages);
})();
